import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Form,
  Modal,
  Spinner,
  Table,
  Toast,
  ToastContainer,
} from 'react-bootstrap';

import {
  deleteVideoMaterial,
  fetchMaterialCategories,
  fetchVideoMaterials,
  updateVideoMaterial,
  uploadVideoMaterial,
} from '@/features/automation/videoEditing/services/materialService.js';
import PageContentLayout from '@/components/layout/PageContentLayout/PageContentLayout.js';
import './VideoMaterialsPage.scss';

const formatTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('zh-CN', { hour12: false });
};

const getMaterialMediaTypeLabel = (mediaType) => {
  if (mediaType === 'video') return '视频';
  if (mediaType === 'font') return '字体';
  return '图片';
};

const createUploadForm = () => ({
  name: '',
  category_id: '',
  is_active: true,
  file: null,
});

const VideoMaterialsPage = () => {
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    category_id: '',
    media_type: '',
    is_active: '',
  });
  const [uploadForm, setUploadForm] = useState(createUploadForm());
  const [editForm, setEditForm] = useState({
    name: '',
    category_id: '',
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [previewMaterial, setPreviewMaterial] = useState(null);
  const [toastInfo, setToastInfo] = useState({
    show: false,
    message: '',
    variant: 'success',
  });

  const showToast = useCallback((message, variant = 'success') => {
    setToastInfo({ show: true, message, variant });
  }, []);

  const activeCategories = useMemo(
    () => categories.filter((category) => category.is_active !== false),
    [categories]
  );

  const editableCategories = useMemo(() => {
    if (!selectedMaterial) return activeCategories;
    return categories.filter(
      (category) =>
        category.is_active !== false ||
        category.id === selectedMaterial.category_id
    );
  }, [activeCategories, categories, selectedMaterial]);

  const loadCategories = useCallback(async () => {
    try {
      const data = await fetchMaterialCategories({ include_inactive: true });
      setCategories(data);
      setUploadForm((current) => ({
        ...current,
        category_id:
          current.category_id ||
          data.find((category) => category.is_active !== false)?.id ||
          '',
      }));
    } catch (error) {
      showToast(error.response?.data?.message || '获取分类失败', 'danger');
    }
  }, [showToast]);

  const loadMaterials = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.category_id) params.category_id = filters.category_id;
      if (filters.media_type) params.media_type = filters.media_type;
      if (filters.is_active !== '') params.is_active = filters.is_active;
      const data = await fetchVideoMaterials(params);
      setMaterials(data);
    } catch (error) {
      showToast(error.response?.data?.message || '获取素材失败', 'danger');
    } finally {
      setLoading(false);
    }
  }, [filters, showToast]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadMaterials();
  }, [loadMaterials]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleUploadChange = (event) => {
    const { name, value, checked, type, files } = event.target;
    setUploadForm((current) => {
      if (files) {
        const file = files[0] || null;
        return {
          ...current,
          [name]: file,
          name: file?.name || current.name,
        };
      }

      return {
        ...current,
        [name]: type === 'checkbox' ? checked : value,
      };
    });
  };

  const handleEditChange = (event) => {
    const { name, value, checked, type } = event.target;
    setEditForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const openUploadModal = () => {
    setUploadForm({
      ...createUploadForm(),
      category_id: activeCategories[0]?.id || '',
    });
    setShowUploadModal(true);
  };

  const closeUploadModal = () => {
    if (saving) return;
    setShowUploadModal(false);
  };

  const openEditModal = (material) => {
    setSelectedMaterial(material);
    setEditForm({
      name: material.name || '',
      category_id: material.category_id || '',
      is_active: material.is_active !== false,
    });
  };

  const closeEditModal = () => {
    if (saving) return;
    setSelectedMaterial(null);
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!uploadForm.category_id) {
      showToast('请选择分类', 'danger');
      return;
    }
    if (!uploadForm.name.trim()) {
      showToast('请输入素材名称', 'danger');
      return;
    }
    if (!uploadForm.file) {
      showToast('请选择素材文件', 'danger');
      return;
    }

    const payload = new FormData();
    payload.append('name', uploadForm.name.trim());
    payload.append('category_id', uploadForm.category_id);
    payload.append('is_active', String(uploadForm.is_active));
    payload.append('material_file', uploadForm.file);

    setSaving(true);
    try {
      await uploadVideoMaterial(payload);
      setShowUploadModal(false);
      setUploadForm(createUploadForm());
      await loadMaterials();
      showToast('素材已上传');
    } catch (error) {
      showToast(error.response?.data?.message || '上传素材失败', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedMaterial) return;
    if (!editForm.name.trim()) {
      showToast('请输入素材名称', 'danger');
      return;
    }
    if (!editForm.category_id) {
      showToast('请选择分类', 'danger');
      return;
    }

    setSaving(true);
    try {
      await updateVideoMaterial(selectedMaterial.id, {
        name: editForm.name.trim(),
        category_id: editForm.category_id,
        is_active: editForm.is_active,
      });
      setSelectedMaterial(null);
      await loadMaterials();
      showToast('素材已更新');
    } catch (error) {
      showToast(error.response?.data?.message || '保存素材失败', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteCandidate) return;

    setSaving(true);
    try {
      await deleteVideoMaterial(deleteCandidate.id);
      setDeleteCandidate(null);
      await loadMaterials();
      showToast('素材已删除');
    } catch (error) {
      showToast(error.response?.data?.message || '删除素材失败', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const renderMedia = (material) => (
    <button
      type="button"
      className="material-preview-trigger"
      onClick={() => setPreviewMaterial(material)}
    >
      {material.media_type === 'video' ? (
        <video
          src={material.url}
          className="material-thumb"
          muted
          playsInline
          preload="metadata"
        />
      ) : material.media_type === 'font' ? (
        <span className="material-thumb material-thumb--font" aria-label="字体文件">
          <i className="bi bi-fonts" />
        </span>
      ) : (
        <img
          src={material.url}
          className="material-thumb"
          alt={material.name || material.category_name || '素材'}
        />
      )}
      <span className="material-meta">
        <span className="material-name" title={material.name || '-'}>
          {material.name || '-'}
        </span>
        <span className="material-media-type">
          {getMaterialMediaTypeLabel(material.media_type)}
        </span>
      </span>
    </button>
  );

  return (
    <>
      <ToastContainer position="top-center" className="p-3">
        <Toast
          show={toastInfo.show}
          onClose={() => setToastInfo({ ...toastInfo, show: false })}
          delay={3000}
          autohide
          bg={toastInfo.variant}
        >
          <Toast.Body
            className={
              toastInfo.variant === 'warning' || toastInfo.variant === 'light'
                ? 'text-dark'
                : 'text-white'
            }
          >
            {toastInfo.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <PageContentLayout
        pageClassName="video-materials-page"
        title="素材库"
        actions={
          <Button
            variant="primary"
            onClick={openUploadModal}
            disabled={saving || activeCategories.length === 0}
          >
            <i className="bi bi-upload" />
            <span>上传素材</span>
          </Button>
        }
        filters={
          <>
            <Form.Select
              name="category_id"
              value={filters.category_id}
              onChange={handleFilterChange}
              aria-label="分类"
            >
              <option value="">全部分类</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              name="media_type"
              value={filters.media_type}
              onChange={handleFilterChange}
              aria-label="媒体类型"
            >
              <option value="">全部媒体</option>
              <option value="image">图片</option>
              <option value="video">视频</option>
              <option value="font">字体</option>
            </Form.Select>
            <Form.Select
              name="is_active"
              value={filters.is_active}
              onChange={handleFilterChange}
              aria-label="状态"
            >
              <option value="">全部状态</option>
              <option value="true">可用</option>
              <option value="false">不可用</option>
            </Form.Select>
          </>
        }
        bodyClassName="materials-table-wrap"
      >
        {loading ? (
          <div className="materials-loading">
            <Spinner animation="border" size="sm" />
            <span>加载中</span>
          </div>
        ) : (
          <Table bordered hover responsive className="materials-table">
            <thead>
              <tr>
                <th>媒体</th>
                <th>分类</th>
                <th>状态</th>
                <th>上传时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((material) => (
                <tr key={material.id}>
                  <td>{renderMedia(material)}</td>
                  <td>{material.category_name || '-'}</td>
                  <td>{material.is_active ? '可用' : '不可用'}</td>
                  <td>{formatTime(material.create_time)}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => openEditModal(material)}
                      disabled={saving}
                    >
                      编辑
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => setDeleteCandidate(material)}
                      disabled={saving}
                    >
                      删除
                    </Button>
                  </td>
                </tr>
              ))}
              {materials.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center">
                    暂无素材
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </PageContentLayout>

      <Modal
        show={showUploadModal}
        onHide={closeUploadModal}
        backdrop={saving ? 'static' : true}
        centered
      >
        <Form onSubmit={handleUpload}>
          <Modal.Header closeButton={!saving}>
            <Modal.Title>上传素材</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>素材名称</Form.Label>
              <Form.Control
                name="name"
                value={uploadForm.name}
                onChange={handleUploadChange}
                disabled={saving}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>分类</Form.Label>
              <Form.Select
                name="category_id"
                value={uploadForm.category_id}
                onChange={handleUploadChange}
                disabled={saving}
                required
              >
                <option value="">请选择分类</option>
                {activeCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>素材文件</Form.Label>
              <Form.Control
                name="file"
                type="file"
                accept="image/*,video/*,.ttf,.otf,.ttc,.otc"
                onChange={handleUploadChange}
                disabled={saving}
                required
              />
            </Form.Group>
            <Form.Check
              type="switch"
              id="material-upload-is-active"
              name="is_active"
              label="可用"
              checked={uploadForm.is_active}
              onChange={handleUploadChange}
              disabled={saving}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={closeUploadModal} disabled={saving}>
              取消
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? '上传中' : '上传'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={!!selectedMaterial}
        onHide={closeEditModal}
        backdrop={saving ? 'static' : true}
        centered
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>编辑素材</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>素材名称</Form.Label>
            <Form.Control
              name="name"
              value={editForm.name}
              onChange={handleEditChange}
              disabled={saving}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>分类</Form.Label>
            <Form.Select
              name="category_id"
              value={editForm.category_id}
              onChange={handleEditChange}
              disabled={saving}
            >
              {editableCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Check
            type="switch"
            id="material-edit-is-active"
            name="is_active"
            label="可用"
            checked={editForm.is_active}
            onChange={handleEditChange}
            disabled={saving}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEditModal} disabled={saving}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSaveEdit} disabled={saving}>
            {saving ? '保存中' : '保存'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!deleteCandidate}
        onHide={() => !saving && setDeleteCandidate(null)}
        backdrop={saving ? 'static' : true}
        centered
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>删除素材</Modal.Title>
        </Modal.Header>
        <Modal.Body>确认删除该素材？</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setDeleteCandidate(null)}
            disabled={saving}
          >
            取消
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={saving}>
            {saving ? '删除中' : '确认删除'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!previewMaterial}
        onHide={() => setPreviewMaterial(null)}
        size="xl"
        centered
        className="material-preview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>媒体预览</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewMaterial?.media_type === 'video' ? (
            <video
              src={previewMaterial.url}
              className="material-preview-media"
              controls
              autoPlay
            />
          ) : previewMaterial?.media_type === 'font' ? (
            <div className="material-preview-font">
              <i className="bi bi-fonts" />
              <span>{previewMaterial?.name || '字体文件'}</span>
              <a href={previewMaterial?.url} target="_blank" rel="noreferrer">
                打开字体文件
              </a>
            </div>
          ) : (
            <img
              src={previewMaterial?.url}
              className="material-preview-media"
              alt={previewMaterial?.name || previewMaterial?.category_name || '素材预览'}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default VideoMaterialsPage;
